/**
 * EFFISLayer.tsx — Overlay WMS EFFIS aree percorse dal fuoco
 *
 * Servizio: European Forest Fire Information System (JRC Copernicus)
 * WMS:      https://maps.effis.emergency.copernicus.eu/effis
 *
 * Layer disponibili:
 *   modis.ba.today   — Oggi
 *   modis.ba.week    — Ultimi 7 giorni
 *   modis.ba.month   — Ultimi 30 giorni
 *   modis.ba.season  — Dall'inizio della stagione antincendio
 *
 * © 2024 IRTILAC — Angelo Marino — Tutti i diritti riservati.
 */

import React, { useEffect, useRef } from 'react';
import { WMSTileLayer } from 'react-leaflet';

const EFFIS_URL = 'https://maps.effis.emergency.copernicus.eu/effis';

export type EFFISTemporalLayer = 'modis.ba.today' | 'modis.ba.week' | 'modis.ba.month' | 'modis.ba.season';
export type EFFISStatus = 'idle' | 'loading' | 'ok' | 'error';

export const EFFIS_LAYER_OPTIONS: { value: EFFISTemporalLayer; label: string }[] = [
  { value: 'modis.ba.today',  label: 'Oggi'           },
  { value: 'modis.ba.week',   label: 'Ultimi 7 giorni' },
  { value: 'modis.ba.month',  label: 'Ultimi 30 giorni'},
  { value: 'modis.ba.season', label: 'Stagione'        },
];

interface EFFISLayerProps {
  visible: boolean;
  temporalLayer?: EFFISTemporalLayer;
  opacity?: number;
  onStatusChange?: (status: EFFISStatus) => void;
}

const EFFISLayer: React.FC<EFFISLayerProps> = ({
  visible,
  temporalLayer = 'modis.ba.season',
  opacity = 0.7,
  onStatusChange,
}) => {
  const errorCountRef = useRef(0);

  // Reset stato quando cambia layer o visibilità
  useEffect(() => {
    if (!visible) { onStatusChange?.('idle'); return; }
    errorCountRef.current = 0;
    onStatusChange?.('loading');
  }, [visible, temporalLayer]); // eslint-disable-line react-hooks/exhaustive-deps

  if (!visible) return null;

  return (
    <WMSTileLayer
      key={`effis-${temporalLayer}`}
      url={EFFIS_URL}
      layers={temporalLayer}
      format="image/png"
      transparent={true}
      opacity={opacity}
      version="1.3.0"
      attribution='© <a href="https://forest-fire.emergency.copernicus.eu" target="_blank" rel="noopener">EFFIS JRC Copernicus</a>'
      eventHandlers={{
        loading:   () => { errorCountRef.current = 0; onStatusChange?.('loading'); },
        load:      () => { onStatusChange?.(errorCountRef.current > 0 ? 'error' : 'ok'); },
        tileerror: () => { errorCountRef.current++; onStatusChange?.('error'); },
      }}
    />
  );
};

export default EFFISLayer;
